import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import ctaBg from '../landing/assets/about_us_4.webp';
import iconApple from '../landing/assets/Apple.png';
import iconGoogle from '../landing/assets/Playstore.png';
import introImg from '../landing/assets/roundnet-tournament/roundnet-match.jpg';
import whyImg from '../landing/assets/sports/League2.png';
import heroBg from '../landing/assets/roundnet-tournament/roundnet-hero.jpg';
import icon1 from '../landing/assets/svgs/scheduling_icon.svg';
import icon2 from '../landing/assets/svgs/sports_league_icon_2.svg';
import icon3 from '../landing/assets/svgs/sports_league_icon_3.svg';
import icon4 from '../landing/assets/svgs/sports_league_icon_4.svg';
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

const bracketSteps = [
    {
        title: "Create Your Competition",
        description: "Set up a roundnet competition in minutes with a dedicated roundnet area built for fast 2v2 play.",
    },
    {
        title: "Add Teams & Players",
        description: "Register your 2-player teams, organize divisions by level or age group, and open online sign-ups.",
    },
    {
        title: "Choose Your Format",
        description: "Pick a league, group stage + knockout cup, or single-elimination bracket — the roundnet fixture generator builds the matchups instantly.",
    },
    {
        title: "Score Game by Game",
        description: "Enter each game (to 21, win by 2) across a best-of-three match. Game wins, standings, and the live view update automatically.",
    },
    {
        title: "Publish Standings",
        description: "Share live tables with match points, games won/lost and a public big-screen view for players and fans.",
    },
];

const FAQs = [
    {
        q: "What is roundnet tournament software?",
        a: <>Roundnet tournament software helps leagues, clubs, and organizers run roundnet (spikeball) competitions digitally — from team registration and division setup to fixture and bracket generation, game-by-game scoring, live standings, and results. ScorePal brings all of this into one centralized platform with a dedicated roundnet area.</>,
    },
    {
        q: "Does ScorePal support best-of-three roundnet scoring?",
        a: <>Yes. ScorePal records each game (to 21, win by 2) across best-of-three roundnet matches — the organizer chooses the format per competition. Game wins, match results, standings, and the live view update instantly as scores come in.</>,
    },
    {
        q: "What roundnet tournament formats can I run?",
        a: <>ScorePal supports round-robin leagues, group stage to knockout cups, and single-elimination brackets, plus season-long leagues. The built-in roundnet fixture generator creates structured matchups automatically and advances winners as results are entered.</>,
    },
    {
        q: "How are roundnet standings calculated?",
        a: <>Roundnet standings use a points table — 3 points for a match win — ranked by match points, then games won and lost, so league tables read the way a roundnet season expects.</>,
    },
    {
        q: "Can I run a roundnet league as well as tournaments?",
        a: <>Yes. ScorePal is both roundnet tournament software and roundnet league software. You can run one-off cups and tournaments and season-long leagues with fixtures, standings, and rankings from the same platform.</>,
    },
    {
        q: "Can players register online and pay entry fees?",
        a: <>Yes. Each competition gets a public registration page where teams can sign up, with optional divisions, custom registration questions, waivers, and entry-fee payments handled through Stripe.</>,
    },
    {
        q: "Does it work on mobile?",
        a: <>Yes. ScorePal works across web, iOS, and Android, so organizers can manage matches and enter game scores courtside, and players and spectators can follow live scores and standings from any device.</>,
    },
    {
        q: "Is ScorePal suitable for club and community roundnet?",
        a: <>Absolutely. ScorePal scales from community meetups and pickup leagues to large multi-division roundnet tournaments, helping organizers reduce admin work and run more professional events.</>,
    },
];

export const Route = createFileRoute('/roundnet-tournament-software')({
    component: RouteComponent,
})

function RouteComponent() {
    const features = [
        {
            icon:        icon1,
            alt:         'roundnet fixture icon',
            title:       'Roundnet Fixture & Bracket Generator',
            description: (
                <>
                    Build league fixtures, round robins, or group-to-knockout cup formats in seconds with a roundnet fixture generator made for fast 2v2 events. Customize the number of groups, teams advancing, and add a third-place playoff or plate competition. You can also create matchups quickly with our {' '}
                    <a href="https://scorepal.co/create-brackets/" target="_blank" rel="noopener noreferrer" className="text-[#082F8A] underline font-semibold">
                        Bracket Generator
                    </a>.
                </>
            ),
        },
        {
            icon:        icon2,
            alt:         'game scoring icon',
            title:       'Game-by-Game Scoring',
            description: 'Record roundnet the way it is played — game by game, to 21 (win by 2), across a best-of-three match. Every game instantly updates the match result, brackets, standings, and the live tournament view, so there is no manual tallying and no delays for players or spectators.',
        },
        {
            icon:        icon3,
            alt:         'standings icon',
            title:       'Real-Time Standings & Live View',
            description: 'Roundnet tables update automatically with a points system, games won and lost — exactly how a roundnet season should read. A public big-screen live view keeps fixtures, scores, and brackets visible on any screen at the venue or online.',
        },
        {
            icon:        icon4,
            alt:         'team management icon',
            title:       'Team & Division Management',
            description: 'Manage 2-player teams and multiple divisions from one dashboard. Open online registration with custom forms, waivers, and entry-fee payments — then run several divisions of one competition side by side without spreadsheets.',
        },
    ];

    const whyChooseItems = [
        'Dedicated roundnet area built for 2v2 competitions',
        'Best-of-three game scoring (to 21, win by 2)',
        'League, cup (groups + knockout), and elimination formats',
        'Points-based standings with games won/lost',
        'Online team registration, divisions & entry-fee payments',
        'Live scoring and a public big-screen tournament view',
        'Works on web, iOS, and Android — score courtside',
    ];

    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context":   "https://schema.org",
                "@type":      "FAQPage",
                "mainEntity": FAQs.map((f) => ({
                    "@type":          "Question",
                    "name":           f.q,
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text":  (typeof f.a === 'string' ? f.a : ''),
                    },
                })),
            }} />
            <JsonLd id={`page${Route.id}/app`} item={{
                "@context":            "https://schema.org",
                "@type":               "SoftwareApplication",
                "name":                "Roundnet Tournament Software",
                "url":                 "https://scorepal.co/roundnet-tournament-software/",
                "applicationCategory": "SportsApplication",
                "operatingSystem":     ["Web", "Android", "iOS"],
                "description":         "Run roundnet (spikeball) tournaments and leagues with automated fixtures, game-by-game scoring, points standings, team registration, and live scoring.",
                "offers":              {
                    "@type":         "Offer",
                    "price":         "0",
                    "priceCurrency": "USD"
                },
                "publisher":           {
                    "@type": "Organization",
                    "name":  "ScorePal",
                    "url":   "https://scorepal.co/"
                }
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Roundnet Tournament Software", item: "https://scorepal.co/roundnet-tournament-software/"}]} />
            <title>Roundnet Tournament Software for Leagues & Clubs | ScorePal</title>
            <meta name="description" content="Run roundnet (spikeball) tournaments and leagues with ScorePal — roundnet fixture generator, game-by-game scoring, points standings, online registration, and live scoring." />
            <link rel="canonical" href={`${config.publicUrl}/roundnet-tournament-software/`} />

            {/* Hero */}
            <section className="relative overflow-hidden p-4">
                <div
                    className="flex flex-col min-h-screen justify-between relative pb-16 rounded-4xl bg-[#EDEEF4] bg-center bg-cover bg-no-repeat saturate-[0.87]"
                    style={{backgroundImage: `url(${heroBg})`}}
                >
                    <div className="absolute inset-0 bg-linear-to-r from-black/60 via-black/30 to-transparent rounded-4xl" />
                    {/* Hero photo is CC BY 2.0 (Paxson Woelber) — the license requires this credit. */}
                    <a href="https://commons.wikimedia.org/wiki/File:Spikeball_on_the_beach._Anchorage,_Alaska_(43775242815).jpg"
                       target="_blank" rel="noopener noreferrer"
                       className="absolute bottom-2 right-4 z-10 text-[10px] text-white/40 hover:text-white/70">
                        Photo: Paxson Woelber · CC BY 2.0
                    </a>

                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8 flex-1 flex flex-col justify-center relative z-10">
                        <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-4">
                            ROUNDNET TOURNAMENT <br />SOFTWARE FOR LEAGUES & CLUBS
                        </h1>

                        <p className="font-inter text-white/90 text-base lg:text-xl mb-8 max-w-2xl leading-relaxed">
                            Run roundnet (spikeball) tournaments and leagues the easy way — automated fixtures, game-by-game scoring, and live standings in one platform.
                        </p>

                        <div className="flex flex-wrap items-center gap-3">
                            <Link
                                to={"/signup/player/" as any}
                                className="flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 w-fit font-semibold hover:bg-white transition"
                            >
                                <span className="font-inter text-base font-medium">Lets Get Started</span>
                                <div className="bg-[#082F8A] rounded-full p-1">
                                    <ArrowUpRightIcon className="text-white" size={28} />
                                </div>
                            </Link>
                            <Link
                                to="/find-tournaments/"
                                className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10"
                            >
                                Browse Tournaments
                            </Link>
                        </div>

                        <div className="flex flex-wrap gap-4 mt-16 lg:mt-60">
                            <a href="https://play.google.com/store/apps/details?id=co.scorepal.mobile&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" className="inline-block">
                                <div className="bg-white/10 rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-gray-100 transition">
                                    <img src={iconGoogle} alt="Google Play" className="h-8" />
                                    <div className="text-left">
                                        <div className="text-white/70 text-xs">GET IT ON</div>
                                        <div className="text-white font-semibold text-sm">Google Play</div>
                                    </div>
                                </div>
                            </a>
                            <a href="https://apps.apple.com/us/app/score-pal/id6745509167" target="_blank" rel="noopener noreferrer" className="inline-block">
                                <div className="bg-white/10 rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-gray-900 transition">
                                    <img src={iconApple} alt="App Store" className="h-8" />
                                    <div className="text-left">
                                        <div className="text-white/70 text-xs">Download on the</div>
                                        <div className="text-white font-semibold text-sm">App Store</div>
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Intro */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
                        <div>
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-6">
                                Effortlessly Run Professional <br />Roundnet Tournaments
                            </h2>
                            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed mb-4">
                                Organizing roundnet used to mean spreadsheets for fixtures, paper scoresheets for every game, and chasing results between matches. ScorePal replaces all of it with roundnet tournament software built for the way the game is actually run.
                            </p>
                            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed">
                                Create draws, register your 2v2 teams, score game by game, and watch standings update live — across web, iOS, and Android. Whether you run a weekend cup, a community meetup, or a full season, ScorePal keeps every match, division, and result in one place.
                            </p>
                        </div>
                        <div className="hidden lg:block">
                            <img src={introImg} alt="Roundnet tournament management" className="w-full h-auto rounded-3xl object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8 text-center">
                        Features of Our <span className="text-[#082F8A]">Roundnet</span> Tournament Software
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-12">
                        {features.map((feature, index) => (
                            <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-full bg-[#EDEEF4] flex items-center justify-center shrink-0">
                                        <img src={feature.icon} alt="" className="w-6 h-6" />
                                    </div>
                                    <h3 className="font-inter text-[#082F8A] font-semibold text-base">{feature.title}</h3>
                                </div>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <div className="flex justify-center">
                <Link to={"/signup/player/" as any} className="flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 w-fit font-semibold hover:bg-white transition">
                    <span className="font-inter text-base font-medium">Lets Get Started</span>
                    <div className="bg-[#082F8A] rounded-full p-1">
                        <ArrowUpRightIcon className="text-white" size={28} />
                    </div>
                </Link>
            </div>

            {/* Why choose */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                        <div>
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8">
                                Why Leagues & Clubs <br />Choose ScorePal for Roundnet
                            </h2>
                            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed mb-8">
                                ScorePal is built specifically for team sports, with a dedicated roundnet experience — not a generic event tool. Run tournaments and leagues together, score the way roundnet is played, and give players and fans a live, professional experience.
                            </p>
                            <ul className="space-y-4">
                                {whyChooseItems.map((item, index) => (
                                    <li key={index} className="flex items-center gap-4">
                                        <CheckBadgeIcon size={24} />
                                        <span className="font-inter text-[#1a1a2e] text-base">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="hidden lg:block">
                            <img src={whyImg} alt="Why choose ScorePal for roundnet" className="w-full h-auto rounded-3xl object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Steps CTA */}
            <section className="py-8 lg:py-16">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="rounded-3xl py-16 lg:py-24 px-8 text-center bg-top bg-cover bg-no-repeat" style={{backgroundImage: `url(${ctaBg})`}}>
                        <h2 className="font-bebas text-white uppercase text-[clamp(28px,4vw,48px)] leading-tight font-bold tracking-wide mb-10">
                            Run Your Next Roundnet Tournament in 5 Steps
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                            {bracketSteps.map((step, index) => (
                                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-left">
                                    <div className="w-10 h-10 rounded-full bg-white text-[#082F8A] flex items-center justify-center font-bold mb-4">{index + 1}</div>
                                    <h3 className="font-inter text-white font-semibold text-base mb-3">{step.title}</h3>
                                    <p className="font-inter text-white/80 text-sm leading-relaxed">{step.description}</p>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-center mt-12">
                            <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                                <span className="font-inter text-base font-medium">Lets Get Started</span>
                                <div className="bg-[#082F8A] rounded-full p-1">
                                    <ArrowUpRightIcon className="text-white" size={28} />
                                </div>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Manual vs software */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="text-center max-w-5xl mx-auto mb-12">
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-6">
                            Manual Scoresheets vs Roundnet Tournament Software
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed">
                            Running roundnet by hand means paper scoresheets, spreadsheet fixtures, and standings that are always a match behind. Here's how ScorePal changes that:
                        </p>
                    </div>
                    <div className="overflow-x-auto">
                        <div className="min-w-[800px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-2 bg-[#F6F7FB]">
                                <div className="p-6 border-r border-[#d9dce7]">
                                    <h3 className="font-bebas text-2xl text-[#1a1a2e] text-center">Manual Management <br /><span className="text-[#082F8A]">(Paper & Spreadsheets)</span></h3>
                                </div>
                                <div className="p-6">
                                    <h3 className="font-bebas text-2xl text-[#082F8A] text-center">With ScorePal Roundnet Software</h3>
                                </div>
                            </div>
                            {[
                                ['Fixtures drawn by hand and re-drawn after every result', 'Automatic roundnet fixture & bracket generator that advances winners instantly'],
                                ['Game scores tallied on paper and typed up later', 'Game-by-game scoring with automatic match results'],
                                ['Standings recalculated manually, always out of date', 'Live points table with games won/lost, updated every match'],
                                ['Registration by email and DMs', 'Public registration page with divisions, waivers & payments'],
                                ['Spectators ask "what’s the score?"', 'Public big-screen live view on any device'],
                                ['Data scattered across tools', 'Teams, fixtures & results in one platform'],
                            ].map((row, i) => (
                                <div key={i} className={`grid grid-cols-2 border-t border-[#d9dce7] ${i % 2 ? 'bg-[#FAFBFD]' : ''}`}>
                                    <div className="p-5 border-r border-[#d9dce7] font-inter text-[#1a1a2e]/80">{row[0]}</div>
                                    <div className="p-5 font-inter text-[#1a1a2e] font-medium">{row[1]}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-16">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Make Your Next Roundnet Tournament Seamless
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-4xl mx-auto mb-6">
                        Give your players and fans a professional experience — fast fixtures, accurate game scoring, and live standings without the admin. ScorePal handles the operations so you can focus on the game.
                    </p>
                    <p className="font-inter text-white text-lg font-semibold mb-10">
                        Start managing your roundnet tournaments and leagues with ScorePal today.
                    </p>
                    <div className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full px-5 py-2 font-semibold mb-10">
                        <span className="font-inter text-sm lg:text-base">Roundnet tournament &amp; league software for clubs and organizers</span>
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Lets Get Started</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"FAQs About Roundnet Tournament Software"} faqs={FAQs} />
            <FooterSection />
        </div>
    );
}
